import qs from 'qs';
import type { ForkEffect } from 'redux-saga/effects';

import type { BasePageProps } from '@/core/shell/components/BasePage';
import { HttpStatusCode } from '@/domains/core/httpClient/HttpStatusCode';
import { traceServerSideProps } from '@/domains/core/observability/tracing/traceServerSideProps';
import type { InferPage } from '@/domains/core/page/interfaces/PageProps';
import { updateResponseAndProps } from '@/domains/core/page/utils/updateResponseAndProps';
import { getPlatform, isB2C } from '@/domains/core/settings/utils';
import { getPageProps } from '@/domains/core/shell/page/getPageProps';
import { setGoogleClickIdentifierSessionCookie } from '@/domains/core/tracking/utils/googleClickIdentifier/setGoogleClickIdentifierSessionCookie';
import { reducer as eligibilityCouponsReducer } from '@/domains/loyalty/modules/CouponEligibility/eligibilityCoupons.reducer';
import { articleReviewsReducer } from '@/domains/productDiscovery/ArticleReviews/root.ducks.default';
import { bulkPurchaseReducer } from '@/domains/productDiscovery/ProductPage/common/modules/BulkPurchase/ducks/BulkPurchase.reducer';
import { PAGE_TYPE_PRODUCT } from '@/domains/productDiscovery/ProductPage/constants';
import { GET_DETAIL } from '@/domains/productDiscovery/ProductPage/detail.actions';
import { productPageEffects } from '@/domains/productDiscovery/ProductPage/detail.saga.default';
import { ProductPage } from '@/domains/productDiscovery/ProductPage/ProductPage';
import productPageReducer from '@/domains/productDiscovery/ProductPage/reducer';
import { detailSelector } from '@/domains/productDiscovery/ProductPage/selectors/detail';
import ErrorWithRedirection from '@/domains/productDiscovery/ProductPage/utils/error/ErrorWithRedirection';
import { PRODUCT_RECOMMENDATION } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/constants';
import recommendationReducer from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/ProductRecommendation.reducer';
import { SPONSORED_PRODUCT_RECOMMENDATION } from '@/domains/productDiscovery/SponsoredProductRecommendation/modules/SponsoredProductRecommendation/constants';
import sponsoredProductRecommendationReducer from '@/domains/productDiscovery/SponsoredProductRecommendation/modules/SponsoredProductRecommendation/SponsoredProductRecommendation.reducer';

export const pageType = PAGE_TYPE_PRODUCT;

interface ProductPageProps extends BasePageProps {}

export const Product: InferPage<typeof getServerSideProps> = () => (
  <ProductPage />
);

export const getServerSideProps = traceServerSideProps(async (context) => {
  try {
    const { query, req } = context;

    const reducers = new Map();
    reducers.set('productDiscovery.productPage', productPageReducer);
    reducers.set(
      `productDiscovery.${PRODUCT_RECOMMENDATION}`,
      recommendationReducer,
    );

    reducers.set(
      `productDiscovery.${SPONSORED_PRODUCT_RECOMMENDATION}`,
      sponsoredProductRecommendationReducer,
    );

    reducers.set('productDiscovery.articleReviews', articleReviewsReducer);
    reducers.set(
      'proB2BAnimation.eligibilityCoupon',
      eligibilityCouponsReducer,
    );
    reducers.set('productDiscovery.bulkPurchase', bulkPurchaseReducer);

    const actions = [
      {
        type: GET_DETAIL,
        queryParams: {
          slug: query.slug,
          id: query.id,
          productId: query.product_id,
          modelId: query.model_id,
        },
        req,
      },
    ];

    const sagas: ForkEffect<never>[] = [...productPageEffects];

    const pageProps = await getPageProps(context, {
      sagas,
      reducers,
      actions,
      sagaEnd: false,
    });

    const state = pageProps.initialReduxState;

    const detail = detailSelector(state);
    const { slug, id: articleId, ...rest } = query;
    const writtenArticlePath = `${slug}-${articleId}`;

    const { totalModels, modelId } = detail || {};
    const multipleModels = totalModels && totalModels > 1;

    // Redicrect to the right model when there are multiple models and no modelId is provided in query
    // No redicrect when productId is provided in query
    if (
      getPlatform() === 'es' &&
      multipleModels &&
      modelId &&
      !query.model_id &&
      !query.product_id
    ) {
      const params = `?${qs.stringify({ ...rest, model_id: modelId })}`;
      const destination = `/p/${detail?.articlePath}${params}`;

      return {
        redirect: {
          permanent: true,
          destination,
        },
      };
    }

    if (detail && writtenArticlePath !== detail?.articlePath) {
      const params = Object.keys(rest).length ? `?${qs.stringify(rest)}` : '';

      const destination = `/p/${detail?.articlePath}${params}`;

      return {
        redirect: {
          permanent: true,
          destination,
        },
      };
    }

    if (query.gclid) {
      setGoogleClickIdentifierSessionCookie(context.res, query.gclid);
    }

    return updateResponseAndProps<ProductPageProps>(
      context,
      {
        ...pageProps,
        pageType: 'product',
        pageName: detail?.title || '',
        statusCode: HttpStatusCode.OK,
        pageCustomData: {
          categoryId: detail?.categoryId ?? null,
          modelId: detail?.modelId ?? null,
          // NOTE Use the second level category going from the root in order to
          //      match against chat targeting rules.
          parentCategoryId: detail?.breadcrumbs?.[1]?.id ?? null,
          productPrice: detail?.price?.primaryPrice?.amountWithVat ?? null,
          productReference: detail?.productRef ?? null,
        },
      },
      {
        cacheOptions: isB2C()
          ? {
              sMaxAgeBots: {
                seaBot: 1 * 24 * 60 * 60,
              },
            }
          : undefined,
      },
    );
  } catch (error) {
    if (error instanceof ErrorWithRedirection) {
      return {
        redirect: {
          permanent: false,
          destination: error.redirection,
        },
      };
    }
    const pageProps = await getPageProps(context);

    return updateResponseAndProps<ProductPageProps>(context, {
      ...pageProps,
      pageType,
      pageName: 'product',
      statusCode: error.status || HttpStatusCode.NOT_FOUND,
    });
  }
}, pageType);

export default Product;
